/**
 * LOGIBUS realtime WebSocket client
 * ----------------------------------
 * Lightweight singleton + React hook around a single tenant-scoped
 * WebSocket connection. Features:
 *  - JWT auth via `?token=` query parameter on connect
 *  - Auto reconnect with exponential back-off (1s, 2s, 4s, 8s, max 30s)
 *  - Heartbeat: replies to server-sent ping with pong
 *  - Multiple rooms subscribed dynamically as components mount
 *  - Pub/sub for typed events: `vehicle.position`, `mission.updated`,
 *    `mission.created`, `mission.cancelled`, `mission.duplicated`,
 *    `incident.created`
 *  - Graceful "offline" fallback: callers can keep their existing
 *    polling loop and simply use realtime as a fast-path.
 */
const STORAGE_TOKEN_KEY = "st_access_token";

function wsUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/api/ws/realtime?token=${encodeURIComponent(token)}`;
}

class RealtimeClient {
  constructor() {
    this.ws = null;
    this.status = "idle";       // idle | connecting | open | closed | error
    this.rooms = new Set();
    this.listeners = new Map(); // event -> Set(fn)
    this.statusListeners = new Set();
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.manualClose = false;
  }

  // ── status helpers ─────────────────────────────────────────────
  _setStatus(s) {
    this.status = s;
    this.statusListeners.forEach((fn) => {
      try { fn(s); } catch (_) { /* swallow */ }
    });
  }

  onStatus(fn) {
    this.statusListeners.add(fn);
    fn(this.status);
    return () => this.statusListeners.delete(fn);
  }

  // ── pub/sub ────────────────────────────────────────────────────
  on(event, fn) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(fn);
    return () => this.off(event, fn);
  }

  off(event, fn) {
    const set = this.listeners.get(event);
    if (set) set.delete(fn);
  }

  _emit(event, payload) {
    const set = this.listeners.get(event);
    if (set) set.forEach((fn) => {
      try { fn(payload); } catch (e) { /* eslint-disable no-console */ console.warn("realtime listener error", e); }
    });
    const wildcard = this.listeners.get("*");
    if (wildcard) wildcard.forEach((fn) => fn({ type: event, ...payload }));
  }

  // ── rooms ──────────────────────────────────────────────────────
  subscribe(rooms) {
    rooms.forEach((r) => this.rooms.add(r));
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify({ type: "subscribe", rooms }));
    }
  }

  unsubscribe(rooms) {
    rooms.forEach((r) => this.rooms.delete(r));
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify({ type: "unsubscribe", rooms }));
    }
  }

  // ── connection ─────────────────────────────────────────────────
  connect() {
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    if (!token) {
      this._setStatus("idle");
      return;
    }
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
    this.manualClose = false;
    this._setStatus("connecting");
    let ws;
    try {
      ws = new WebSocket(wsUrl(token));
    } catch (e) {
      this._setStatus("error");
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._setStatus("open");
      if (this.rooms.size > 0) {
        ws.send(JSON.stringify({ type: "subscribe", rooms: Array.from(this.rooms) }));
      }
    };

    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      const type = msg.type;
      if (type === "ping") {
        ws.send(JSON.stringify({ type: "pong" }));
        return;
      }
      if (type === "welcome" || type === "subscribed" || type === "unsubscribed" || type === "pong") {
        this._emit(type, msg);
        return;
      }
      this._emit(type, msg);
    };

    ws.onerror = () => {
      this._setStatus("error");
    };

    ws.onclose = () => {
      this.ws = null;
      this._setStatus("closed");
      if (!this.manualClose) this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    const attempt = this.reconnectAttempts++;
    const delay = Math.min(30000, 1000 * Math.pow(2, attempt));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  close() {
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch (_) { /* noop */ }
      this.ws = null;
    }
    this._setStatus("closed");
  }
}

export const realtime = new RealtimeClient();

// Auto-connect on first import only if a token is present
if (typeof window !== "undefined" && localStorage.getItem(STORAGE_TOKEN_KEY)) {
  realtime.connect();
}

// React hook ----------------------------------------------------------
import { useEffect, useState } from "react";

/**
 * useRealtime — Hook to subscribe to rooms + listen to events.
 *
 * @param {string[]} rooms      — rooms to subscribe while mounted
 * @param {Object} listeners    — { "event.name": fn(payload) }
 * @returns {{status: string}}  — current WS status
 */
export function useRealtime(rooms = [], listeners = {}) {
  const [status, setStatus] = useState(realtime.status);

  useEffect(() => {
    // ensure connection
    realtime.connect();
    const offStatus = realtime.onStatus(setStatus);
    return () => offStatus();
  }, []);

  useEffect(() => {
    if (rooms.length === 0) return;
    realtime.subscribe(rooms);
    return () => realtime.unsubscribe(rooms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rooms)]);

  useEffect(() => {
    const offs = Object.entries(listeners).map(([evt, fn]) => realtime.on(evt, fn));
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(Object.keys(listeners))]);

  return { status };
}
